// **Home's ambient colony background** (`client-home`): a pre-simulated loop replayed through the
// shipped world renderer, behind the entry screen's chrome.
//
// The whole component is a decision about failure. It is **decoration**, so it may never gate or
// delay a CTA, never spin, never surface an error and never shift the layout — a missing,
// oversized, malformed or version-mismatched asset draws the static season-ground fill and says
// nothing (**D10**). What it must *not* do is let that fallback hide a break from the developer:
// the asset's checksum and byte budget are asserted in CI (`ambient-asset.test.ts`, review
// **A5**), which is where a format skew is meant to surface.
//
// **No server request** (`client-home`). Home holds no credential when it first paints, so the
// authenticated trace routes are unreachable to it, and an unauthenticated demo-trace route would
// be a new public read surface for decoration. The bytes arrive bundled, and this component takes
// them as a prop rather than fetching anything.
//
// The renderer factory is an injected seam with **no default**, the `ReviewFrame` precedent: a
// real `CanvasRenderer` needs a GPU context, so a bare mount and every jsdom test render the fill
// with no canvas rather than constructing Pixi.

import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import type { WorldSnapshot } from '../../net/wire/connection';
import type { Camera } from '../../canvas/projection';
import type { RenderInput } from '../../canvas/render-input';
import type { WideColonyContext } from '../../canvas/renderer';
import { performanceClock, rafFrameScheduler, type Clock, type FrameScheduler } from '../../canvas/frame-scheduler';
import { decodeAmbientLoop } from './ambient-format';
import { fieldCss } from './veil';
import { ambientCamera, ambientRenderInput, ambientWorldSnapshot } from './ambient-world';

/** The renderer surface this host drives.
 *
 * Its `render` signature is the **widest** of the shipped ones — the `ReviewRenderer` shape,
 * trailing `wideColonyContext` and all — so the one production factory the composition root
 * already threads satisfies it without an adapter. TypeScript will not assign a six-parameter
 * function to a five-parameter type, so narrowing here would have forced a wrapper whose only job
 * was to drop an argument the ambient loop never has a wide tier to use anyway. */
export interface AmbientRenderer {
  /** `signal` cancels a superseded construction (D-290) — a cancelled `init()` reclaims what it
   * has built and RESOLVES, so cancellation is never reported as a start-up failure. Optional,
   * which is what keeps every existing double that declares `init(): Promise<void>` assignable. */
  init(signal?: AbortSignal): Promise<void>;
  render(
    world: WorldSnapshot,
    camera: Camera,
    elapsedMs: number,
    tierChanged: boolean,
    renderInput?: RenderInput,
    wideColonyContext?: WideColonyContext,
  ): void;
  updateRenderInput(renderInput: RenderInput): void;
  /** Optional because ambient/live hosts run at 1×; `/demo` is the mutable-rate consumer. */
  setPresentationRate?(rate: number): void;
  destroy(): void;
}

export interface AmbientBackgroundProps {
  /** The bundled loop's bytes, or `null` when the asset is absent. Malformed bytes are the same
   * outcome as absent ones — both degrade, neither complains. */
  bytes: Uint8Array | null;
  /**
   * `tickRateHz` is sourced from the decoded loop's own `tickRateHz` (`ambient-format.ts`) —
   * the rate the bundled asset was sampled at — never the generated mirror: this is a
   * **pre-handshake render** (client-tick-rate-provenance, tick-rate-single-source E4), and
   * the loop's own header is a more specific source of truth than the mirror would be.
   */
  createRenderer?: (container: HTMLElement, tickRateHz: number) => AmbientRenderer;
  /** Frame + clock seams, so playback is testable without a display. */
  frameScheduler?: FrameScheduler;
  clock?: Clock;
}

/** The measured viewport the camera is built against, before a real one is known. Portrait, the
 * kit's only orientation. */
const FALLBACK_VIEWPORT = { width: 390, height: 844 };

export function AmbientBackground({
  bytes,
  createRenderer,
  frameScheduler,
  clock,
}: AmbientBackgroundProps) {
  // Decoded **once per byte identity**, never per render: a decode is the only expensive thing
  // this component does, and a loop rebuilt each render would restart the playhead every frame.
  const loop = useMemo(() => (bytes === null ? null : decodeAmbientLoop(bytes)), [bytes]);
  /**
   * A renderer that could not start.
   *
   * **A fourth way to have no picture, and it must look like the other three** (implementation
   * review). A device with no usable GPU context decodes the asset perfectly and then fails at
   * `init()`; before this the component stayed in its `playing` state with a transparent host, so
   * the screen showed *nothing* behind the chrome rather than the season-ground fill — a blank
   * layer, which is the one outcome **D10** rules out. The decode-failure paths were tested and
   * this one was not, so it survived.
   */
  const [rendererFailed, setRendererFailed] = useState(false);
  const decoded = loop !== null && loop.frames.length > 0;
  const playing = decoded && !rendererFailed;

  const hostRef = useRef<HTMLDivElement | null>(null);
  // The renderer is **state**, not a ref: the playback effect below has to start when the
  // renderer finishes initialising, and a ref would leave that effect never re-running.
  const [renderer, setRenderer] = useState<AmbientRenderer | null>(null);
  const frameRef = useRef(0);

  // Own the renderer's lifetime, keyed on the factory alone so nothing about playback tears the
  // Pixi application down and rebuilds it.
  useEffect(() => {
    const host = hostRef.current;
    // `loop === null` is implied by `!playing` already, but stated explicitly so `loop.tickRateHz`
    // below narrows rather than reading through a possibly-null value.
    if (host === null || createRenderer === undefined || !playing || loop === null) {
      return undefined;
    }
    let disposed = false;
    let built: AmbientRenderer;
    try {
      built = createRenderer(host, loop.tickRateHz);
    } catch (error) {
      // A factory that throws synchronously is the same outcome as one whose `init()` rejects.
      console.error('waggle: the ambient background renderer could not be constructed', error);
      setRendererFailed(true);
      return undefined;
    }
    // D-290. This host needed the abort more than the ones the row was raised against, not less:
    // its cleanup destroys through `setRenderer((current) => current?.destroy())`, and `current`
    // is null until `init()` resolves — so a run cancelled mid-construction was reclaimed by
    // nothing at all until its own `.then()` fired, and by nothing ever if the sprite load hung.
    const controller = new AbortController();
    void built.init(controller.signal).then(
      () => {
        if (controller.signal.aborted) {
          // Guarded: the renderer has already reclaimed itself on the abort, so this is a second,
          // idempotent call — but this handler has no `.catch` behind it, so an exception here
          // would surface as an unhandled rejection rather than as anything diagnosable.
          try {
            built.destroy();
          } catch (teardownError: unknown) {
            console.error(
              'waggle: the ambient background renderer failed to tear down',
              teardownError,
            );
          }
          return;
        }
        setRenderer(built);
      },
      // A renderer that cannot start leaves the fill and the chrome exactly as they were. Said
      // once, rather than surfacing as an unhandled rejection — and never to the player, who is
      // looking at a background that still looks deliberate.
      //
      // The **two-argument** `then`, not the trailing `.catch` this used to be. A chained catch
      // also catches whatever the fulfilment handler above throws, and would then report a
      // teardown or a state-setter fault as "the renderer failed to start" — sending the next
      // person to debug it to the wrong half of the lifecycle, and (where `disposed` is still
      // false) flipping the screen to its permanent failed-renderer fallback over something that
      // was not a start-up failure at all. Only an `init()` rejection is an init failure; the
      // three other hosts already draw that line and this one now does too.
      (error: unknown) => {
        // Said to the **developer** and not to the player: the console keeps the failure
        // diagnosable, while the screen falls back to the same static fill a missing asset gets —
        // no spinner, no error, no retry, no shifted layout.
        console.error('waggle: the ambient background renderer failed to start', error);
        if (!disposed) {
          setRendererFailed(true);
        }
      },
    );
    return () => {
      disposed = true;
      // The only thing that can reach a construction still in flight (D-290) — the state setter
      // below sees `null` until `init()` has resolved.
      controller.abort();
      setRenderer((current) => {
        current?.destroy();
        return null;
      });
    };
  }, [createRenderer, playing]);

  // The playback loop. A *cadence* is a wall-clock decision, so the seams are injected exactly as
  // the sandbox's playhead loop injects them; the loop wraps rather than ending, and the wrap is
  // signalled to the renderer through `RenderInput.playhead` so interpolation history resets
  // instead of tweening every bee across the field at the seam.
  useEffect(() => {
    if (loop === null || renderer === null) {
      return undefined;
    }
    const frames = frameScheduler ?? rafFrameScheduler;
    const now = clock ?? performanceClock;
    const stepMs = 1000 / loop.tickRateHz;
    let last = now.now();
    let stopped = false;
    let handle: number | null = null;

    const draw = (): void => {
      const world = ambientWorldSnapshot(loop, frameRef.current);
      if (world === null) {
        return;
      }
      renderer.render(
        world,
        ambientCamera(loop, viewportOf(hostRef.current)),
        stepMs,
        false,
        ambientRenderInput(loop, frameRef.current),
      );
    };

    const tick = (): void => {
      if (stopped) {
        return;
      }
      const at = now.now();
      if (at - last >= stepMs) {
        last = at;
        frameRef.current += 1;
        draw();
      }
      handle = frames.schedule(tick);
    };

    draw();
    handle = frames.schedule(tick);
    return () => {
      stopped = true;
      if (handle !== null) {
        frames.cancel(handle);
      }
    };
    // `loop` and `renderer` are the effect's subjects; the seams are DI, injected once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loop, renderer]);

  return (
    <div
      data-testid="home-ambient"
      data-state={playing ? 'playing' : 'static'}
      style={playing ? HOST_STYLE : STATIC_FILL_STYLE}
    >
      {playing && createRenderer !== undefined && (
        <div ref={hostRef} data-testid="home-ambient-canvas" style={HOST_STYLE} />
      )}
    </div>
  );
}

function viewportOf(host: HTMLElement | null): { width: number; height: number } {
  if (host === null) {
    return FALLBACK_VIEWPORT;
  }
  const width = host.clientWidth || FALLBACK_VIEWPORT.width;
  const height = host.clientHeight || FALLBACK_VIEWPORT.height;
  return { width, height };
}

const HOST_STYLE: CSSProperties = { position: 'absolute', inset: 0, overflow: 'hidden' };

/** The degraded state: a flat summer season-ground fill, the same colour the renderer's own
 * ground quad would draw. Identical layout, identical chrome — panel D of
 * `background-legibility.html` differs from panel C only in that the ground is flat and the bees
 * are gone, and nothing announces it. */
const STATIC_FILL_STYLE: CSSProperties = {
  ...HOST_STYLE,
  background: fieldCss(),
};
